import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import { PORTFOLIO_BUDGET, sumEffects, weightedAverage, annualCouponIncome } from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "bond-bastion";

function formatMoney(n) {
  const sign = n < 0 ? "-" : "";
  return sign + Math.abs(Math.round(n)).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function computeOutcome(reconResult, plan, campaignResult) {
  const { allocations, structure } = plan;
  const { log } = campaignResult;

  const weightedDuration = weightedAverage(allocations, "duration");
  const priceImpactPct = weightedDuration * 1; // a 1% rate hike
  const priceImpactDollar = -(PORTFOLIO_BUDGET * (priceImpactPct / 100));

  const rateHikeChoice = log[0]?.choice;
  const panicSold = !!rateHikeChoice?.effect?.panicSold;

  let realizedRateImpact;
  if (panicSold) {
    realizedRateImpact = priceImpactDollar;
  } else if (structure === "individual") {
    realizedRateImpact = 0;
  } else {
    realizedRateImpact = priceImpactDollar * 0.3;
  }

  const creditChoice = log[1]?.choice;
  const junkAllocation = allocations.junk || 0;
  const additionalJunkBad = creditChoice?.effect?.doubleDownJunk ? junkAllocation * 0.5 : 0;
  const creditImpact = junkAllocation > 0 ? -0.08 * (junkAllocation + additionalJunkBad) : 0;

  const inflationChoice = log[2]?.choice;
  const tipsAllocation = allocations.tips || 0;
  const inflationBenefit = inflationChoice?.effect?.inflationChoice === "tips" ? tipsAllocation * 0.03 : 0;

  const structureChoice = log[4]?.choice;
  const structureImpact = structureChoice?.effect?.earlySell ? -Math.abs(priceImpactDollar) * 0.5 : 0;

  const oneTimeGains = sumEffects(log, "oneTimeGain");
  const oneTimeCosts = sumEffects(log, "oneTimeCost");

  const portfolioValueChange = realizedRateImpact + structureImpact + creditImpact + inflationBenefit + oneTimeGains - oneTimeCosts;
  const finalPortfolioValue = PORTFOLIO_BUDGET + portfolioValueChange;
  const couponIncome = annualCouponIncome(allocations);
  const totalReturn = portfolioValueChange + couponIncome;

  const goodChoices = log.filter((l) => l.choice.tag === "good").length;
  const badChoices = log.filter((l) => l.choice.tag === "bad").length;

  const reconScore = reconResult.correctCount * 15;
  const choiceScore = goodChoices * 20 - badChoices * 20;
  const totalReturnBonus = Math.max(-100, Math.min(400, Math.round(totalReturn / 2)));
  const score = Math.max(50, reconScore + choiceScore + totalReturnBonus + 50);

  return {
    weightedDuration,
    priceImpactDollar,
    realizedRateImpact,
    creditImpact,
    inflationBenefit,
    oneTimeGains,
    oneTimeCosts,
    finalPortfolioValue,
    couponIncome,
    totalReturn,
    goodChoices,
    badChoices,
    score,
  };
}

export default function DebriefStage({ reconResult, plan, campaignResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(reconResult, plan, campaignResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Bond Bastion results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">SIEGE DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">
        {plan.structure === "individual" ? "Individual Bonds" : "Bond Fund"}
      </p>

      <div className="mx-auto mb-4 max-w-sm border-2 border-gold bg-gold/10 p-5">
        <p className="label-pixel mb-1 text-gold">Total Return (1 Year)</p>
        <p className={`font-display text-2xl ${outcome.totalReturn >= 0 ? "text-gold" : "text-danger"}`}>
          {formatMoney(outcome.totalReturn)}
        </p>
        <p className="mt-2 text-[11px] text-mist/60">
          Coupon income + price change, on a {formatMoney(PORTFOLIO_BUDGET)} portfolio with ~{outcome.weightedDuration.toFixed(1)}-year weighted duration.
        </p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Recon Accuracy</span>
          <span className="text-cyan">{reconResult.correctCount}/6</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{outcome.goodChoices}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{outcome.badChoices}</span>
          </span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Coupon Income Earned</span>
          <span className="text-lime">{formatMoney(outcome.couponIncome)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Rate-Hike Price Impact (realized)</span>
          <span>{formatMoney(outcome.realizedRateImpact)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Credit Downgrade Impact</span>
          <span>{formatMoney(outcome.creditImpact)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Inflation Protection Benefit</span>
          <span className="text-lime">{formatMoney(outcome.inflationBenefit)}</span>
        </div>
        <div className="flex justify-between border-t border-black/50 pt-2">
          <span className="text-mist/60">Ending Portfolio Value</span>
          <span className="text-gold">{formatMoney(outcome.finalPortfolioValue)}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        Notice that an unsold, held-to-maturity bond's price dip never shows up as a real loss
        here — only panic-selling turned a paper dip into a permanent one.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ REBUILD THE BASTION
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
